import React, { useEffect, useState } from "react";
import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import BulletIndicatorIcon from "@material-ui/icons/FiberManualRecord";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import Accordion from "components/Accordion";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import MediaPlayButton from "components/MediaPlayButton";
import enumParser from "utils/enum-parser";
import { determineCourseSurface } from "components/Conditions/CourseSurface/course-surface";

import useStyles from "./styles";

export interface WorkoutsProps {
  horse: racehorse360.IHorse;
  pagination?: boolean;
  rootRef: any;
  workouts: racehorse360.IWorkout[];
  isLoading: boolean;
  onLoad?: (items, pagingInfo?) => void;
}

const PAGE_SIZE = 7;

const Workouts = (props: WorkoutsProps) => {
  const { pagination, rootRef, workouts, isLoading, onLoad } = props;
  const classes = useStyles();
  const { currentUser } = useLoggedInUser();
  const [activePage, setActivePage] = useState<number>(1);
  /*const [anchorEl, setAnchorEl] = useState(null)
  const [activeWorkout, setActiveWorkout] = useState<racehorse360.IWorkout>(
    null
  )*/
  const isVeterinarian = currentUser.isVeterinarian;

  /*const handleWorkoutChange = (workout: racehorse360.IWorkout) => {
    if (workout.facility) {
      const temp = [...workouts]
      const index = temp.findIndex(w => w.id === workout.id)

      temp[index] = {
        ...workout
      }

      //setWorkouts(temp)
    } else {
      //setWorkouts(workouts.filter(w => w.id !== workout.id))
    }
  }*/

  const handleVideoOpen =
    (workout: racehorse360.IWorkout) => (e: React.MouseEvent) => {
      return null;
    };

  /*const handleAnalysisClick = () => {}

  const handleMenuOpen = (workout: racehorse360.IWorkout) => (
    e: React.MouseEvent
  ) => {
    setActiveWorkout(workout)
    setAnchorEl(e.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }*/

  const renderWorkoutDetails = (workout: racehorse360.IWorkout) => {
    const isBestTime = workout.ranking === 1;
    const isGate = Boolean(workout.gateIndicator);
    const isDog = Boolean(workout.dogIndicator);
    const workoutDistance = Math.round((workout.distance || 0) * 0.01);
    const workoutDistanceUnit = enumParser.parseDistanceUnit(
      workout.distanceUnit
    );
    const workoutTrackCondition = enumParser.parseTrackCondition(
      workout.trackCondition
    );
    const workoutType = enumParser.parseWorkoutType(workout.workoutType);

    const workoutTimingReversed = String(workout.timing || 0)
      .split("")
      .reverse();
    const workoutTiming = Array.from(workoutTimingReversed, (char, index) => {
      if (index % 2) {
        return ":" + char;
      } else {
        return char;
      }
    })
      .reverse()
      .join("");

    const workoutCourseSurface = determineCourseSurface(workout, classes);

    return (
      <>
        {isBestTime && (
          <BulletIndicatorIcon className={classes.bulletIndicator} />
        )}
        {workoutCourseSurface && (
          <span className={classes.workoutDetailsItem}>
            {workoutCourseSurface}
          </span>
        )}
        {(workoutDistance || workoutDistanceUnit) && (
          <span className={classes.workoutDetailsItem}>
            {workoutDistance || "-"}
            {workoutDistanceUnit || "-"}
          </span>
        )}
        {workoutTrackCondition && (
          <span className={classes.workoutDetailsItem}>
            {workoutTrackCondition}
          </span>
        )}
        {workoutTiming && (
          <span className={classes.workoutDetailsItem}>{workoutTiming}</span>
        )}
        {(workoutType || isGate || isDog) && (
          <span className={classes.workoutDetailsItem}>
            {workoutType}
            {isGate ? "g" : ""}
            {isDog ? "(d)" : ""}
          </span>
        )}
        {(workout.ranking || workout.numberInRankingGroup) && (
          <span className={classes.workoutDetailsItem}>
            {workout.ranking || "-"}/{workout.numberInRankingGroup || "-"}
          </span>
        )}
      </>
    );
  };

  const renderRows = () => {
    const sliced = pagination
      ? workouts?.slice(
          (activePage - 1) * PAGE_SIZE,
          (activePage - 1) * PAGE_SIZE + PAGE_SIZE
        )
      : workouts;

    return sliced.map((workout, i) => (
      <TableRow key={workout.id} className={classes.workoutRow}>
        <TableCell
          className={clsx(classes.workoutCell, classes.workoutDate, {
            [classes.workoutCellVet]: isVeterinarian
          })}
        >
          {format(parseISO(workout.date), "MM/dd/yy")}
        </TableCell>
        <TableCell
          className={clsx(classes.workoutCell, classes.workoutFacility, {
            [classes.workoutCellVet]: isVeterinarian
          })}
        >
          {workout.facility.code}
        </TableCell>
        <TableCell className={classes.workoutDetails}>
          {renderWorkoutDetails(workout)}
        </TableCell>
        <TableCell
          className={clsx(classes.workoutCell, classes.workoutScheduled, {
            [classes.workoutCellVet]: isVeterinarian
          })}
        >
          {workout.comment}
        </TableCell>
        <TableCell
          className={clsx(classes.workoutCell, classes.workoutMenu, {
            [classes.workoutCellVet]: isVeterinarian
          })}
        />
        <TableCell
          className={clsx(
            classes.workoutCell,
            classes.workoutReplay,
            classes.hidden,
            {
              [classes.workoutCellVet]: isVeterinarian
            }
          )}
        >
          <MediaPlayButton
            onOpenMedia={handleVideoOpen(workout)}
            isDisabled={true}
          />
        </TableCell>
        <TableCell
          className={clsx(classes.workoutCell, classes.workoutAnalysis, {
            [classes.workoutCellVet]: isVeterinarian
          })}
        >
          {/*<IconButton //TODO: is commented for MVP
            size="small"
            onClick={handleAnalysisClick}
            disabled={false}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>*/}
        </TableCell>
      </TableRow>
    ));
  };

  const renderTable = () => {
    return workouts.length ? (
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.workout}>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
    ) : (
      <div className={classes.emptyDataHolder}>No Workouts</div>
    );
  };

  useEffect(() => {
    if (!isLoading && onLoad) {
      onLoad(workouts);
    }
  }, [isLoading, workouts.length]);

  if (isLoading) {
    return <Loader className={classes.loader} />;
  }

  return (
    <>
      {pagination ? (
        <>
          {renderTable()}
          <Pagination
            totalCount={workouts?.length}
            pageNumber={activePage}
            pageSize={PAGE_SIZE}
            onPageChange={setActivePage}
          />
        </>
      ) : (
        <Accordion
          count={workouts?.length}
          name={"Workouts"}
          rootRef={rootRef}
          disabled={(!workouts?.length && !isLoading) || isLoading}
        >
          {renderTable()}
        </Accordion>
      )}
      {/*<ScheduleDateMenu
        horse={horse}
        onChange={handleWorkoutChange}
        anchorEl={anchorEl}
        workoutData={activeWorkout}
        onClose={handleMenuClose}
      />*/}
    </>
  );
};

export default React.memo(Workouts);
